import type { ComponentInit } from "./registry";

const GROUP = "[data-fa-cascade]";
const ITEM = "[data-fa-cascade-item]";
const WORDS = "[data-fa-word-reveal]";
const VISIBLE = "fa-cascade--visible";
const INDEX = "--fa-cascade-i";
const THRESHOLD = 0.15;
const PAINTED = "painted";
const WORD = "fa-word";
/* Only RevealWord itself renders this — its own JSX children, never a vendor's hand-authored
   markup — so walking every text node in the subtree is safe here and only here. */
const DEEP_WALK = "fa-word-reveal";

const matchesSelf = (root: ParentNode, selector: string): root is ParentNode & HTMLElement =>
	root instanceof HTMLElement && root.matches(selector);

const isAlreadyVisible = (element: HTMLElement): boolean => {
	const { top, bottom, height } = element.getBoundingClientRect();

	if (height === 0) return false;

	const visible = Math.min(bottom, window.innerHeight) - Math.max(top, 0);

	return visible / height >= THRESHOLD;
};

/** One `.fa-word` item, indexed for the cascade stagger — the index is overwritten by
    `initCascade`'s own pass when nested in a group, so it only has to be correct for a caller
    that reveals outside one entirely. Exported so a host that rebuilds its own text without a
    surrounding cascade group — ArticleHero's tab switcher — builds the same markup cascade.css
    already styles, and can replay it with `replayCascade`. */
export const buildWord = (word: string, index: number): HTMLSpanElement => {
	const outer = document.createElement("span");
	const inner = document.createElement("span");

	outer.className = WORD;
	outer.setAttribute("data-fa-cascade-item", "");
	outer.style.setProperty(INDEX, String(index));
	inner.textContent = word;
	outer.append(inner);

	return outer;
};

/** Every word shares one running index off `startIndex`, so a reveal split across several
    elements still staggers as one sequence. */
export const buildWords = (text: string, startIndex = 0): DocumentFragment => {
	const fragment = document.createDocumentFragment();

	text.split(/\s+/)
		.filter(Boolean)
		.forEach((word, position) => {
			if (position > 0) fragment.append(" ");
			fragment.append(buildWord(word, startIndex + position));
		});

	return fragment;
};

/* Only a lone text node: on the vendor's page the heading is their markup, and splitting
   textContent would delete an authored <strong> with nothing to report it. */
const splittable = (element: HTMLElement): Text | null => {
	const [child] = element.childNodes;

	return element.childNodes.length === 1 && child instanceof Text ? child : null;
};

const splitShallow = (element: HTMLElement, text: Text): (() => void) | undefined => {
	const words = text.data.split(/\s+/).filter(Boolean);

	/* A single word still reveals — the clip-rise is the effect, the stagger is incidental — so
	   only empty text bails. */
	if (words.length === 0) return undefined;

	element.replaceChildren(buildWords(words.join(" ")));

	return () => element.replaceChildren(text.data);
};

/* Walks every text node in the subtree, not just a lone one — a team name, then "Vs", then
   another team name. Safe only because DEEP_WALK marks an element as RevealWord's own JSX. */
const splitDeep = (element: HTMLElement): (() => void) | undefined => {
	const original = element.innerHTML;
	const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
	const textNodes: Text[] = [];

	for (let node = walker.nextNode(); node; node = walker.nextNode()) {
		if (node instanceof Text && node.data.trim() !== "") textNodes.push(node);
	}

	if (textNodes.length === 0) return undefined;

	let index = 0;

	for (const textNode of textNodes) {
		const words = textNode.data.trim().split(/\s+/);

		textNode.replaceWith(buildWords(words.join(" "), index));
		index += words.length;
	}

	return () => {
		element.innerHTML = original;
	};
};

const isPainted = (group: ParentNode): boolean =>
	group instanceof HTMLElement && group.getAttribute("data-fa-cascade") === PAINTED;

/* Measured at reveal, never at init: an element with no layout reports a zero rect, so every
   pair ties and the sort silently keeps document order. Re-measured on replay for the same
   reason — a panel revealed at one breakpoint is reshown at another. */
const indexPainted = (items: HTMLElement[]): void => {
	[...items]
		.sort((first, second) => {
			const a = first.getBoundingClientRect();
			const b = second.getBoundingClientRect();

			return a.top - b.top || a.left - b.left;
		})
		.forEach((item, position) => item.style.setProperty(INDEX, String(position)));
};

export const initCascade: ComponentInit = (group) => {
	/* A word-reveal element is usually a descendant (a heading beside a description and a CTA);
	   RevealWord also stamps `data-fa-cascade` on itself for the standalone case — a Hero
	   headline with nothing else to cascade — so it is its own group and matches here too. */
	const wordReveals = [
		...(matchesSelf(group, WORDS) ? [group] : []),
		...[...group.querySelectorAll<HTMLElement>(WORDS)].filter(
			(element) => element.closest(GROUP) === group,
		),
	];

	const restores = wordReveals
		.map((element) => {
			if (element.classList.contains(DEEP_WALK)) return splitDeep(element);

			const text = splittable(element);

			return text === null ? undefined : splitShallow(element, text);
		})
		.filter((restore) => restore !== undefined);

	/* Scoped to the nearest group, so a cascade nested in another cascade keeps its own
	   sequence instead of both claiming every item. Runs after the split above, so a word-reveal
	   element's own newly-built `.fa-word` items are already present to be found here. */
	const items = [...group.querySelectorAll<HTMLElement>(ITEM)].filter(
		(item) => item.closest(GROUP) === group,
	);

	if (items.length === 0) {
		for (const restore of restores) restore();

		return;
	}

	items.forEach((item, index) => item.style.setProperty(INDEX, String(index)));

	const painted = isPainted(group);

	const reveal = () => {
		if (painted) indexPainted(items);

		for (const item of items) item.classList.add(VISIBLE);
	};

	/* A Hero heading is on screen the moment this runs, and the observer's first callback is
	   still a real async round trip even then — stacked on a Hero's own base delay, that trip
	   alone read as the reveal starting late. Skip it outright when already in view. */
	let observer: IntersectionObserver | undefined;

	if (isAlreadyVisible(group)) {
		reveal();
	} else {
		observer = new IntersectionObserver(
			(records) => {
				if (!records.some((record) => record.isIntersecting)) return;

				reveal();
				observer?.disconnect();
			},
			{ threshold: THRESHOLD },
		);

		observer.observe(group);
	}

	return () => {
		observer?.disconnect();

		for (const restore of restores) restore();

		for (const item of items) {
			item.classList.remove(VISIBLE);
			item.style.removeProperty(INDEX);
		}
	};
};

/* A group's own IntersectionObserver disconnects after its first reveal, so a host that toggles
   visibility itself (a tab panel, not scroll) gets no second entry to fire on — this replays it
   directly. Two frames, not one: the removal must paint before the re-add or the browser
   coalesces both into a no-op and the transition never starts. */
export const replayCascade = (group: HTMLElement): void => {
	const items = [...group.querySelectorAll<HTMLElement>(ITEM)].filter(
		(item) => item.closest(GROUP) === group,
	);

	if (items.length === 0) return;

	for (const item of items) item.classList.remove(VISIBLE);

	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			if (isPainted(group)) indexPainted(items);

			for (const item of items) item.classList.add(VISIBLE);
		});
	});
};
